import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import { getLocale } from './i18n';

export function initCookieConsent(onAccept, onReject) {
  const currentLocale = getLocale();

  CookieConsent.run({
    // Sprachkonfiguration
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true,
        flipButtons: false
      }
    },

    // Callback wenn User Auswahl trifft
    onConsent: ({ cookie }) => {
      if (cookie.categories.includes('analytics')) {
        onAccept();
      } else {
        onReject();
      }
    },

    // Callback wenn User Einstellungen ändert
    onChange: ({ cookie, changedCategories }) => {
      if (changedCategories.includes('analytics')) {
        if (cookie.categories.includes('analytics')) {
          onAccept();
        } else {
          // Analytics deaktivieren
          onReject();
        }
      }
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true
      },
      analytics: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [
            {
              name: /^_ga/
            },
            {
              name: '_gid'
            }
          ]
        }
      }
    },

    language: {
      default: currentLocale,
      autoDetect: 'document',
      translations: {
        de: {
          consentModal: {
            title: 'Wir verwenden Cookies',
            description: 'Diese Website verwendet Cookies, um die Nutzererfahrung zu verbessern und Besucherstatistiken zu analysieren. Du kannst deine Einstellungen jederzeit anpassen.',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige',
            showPreferencesBtn: 'Einstellungen',
            footer: '<a href="/privacy">Datenschutzerklärung</a> <a href="/imprint">Impressum</a>'
          },
          preferencesModal: {
            title: 'Cookie-Einstellungen',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Nur notwendige',
            savePreferencesBtn: 'Auswahl speichern',
            closeIconLabel: 'Schließen',
            serviceCounterLabel: 'Service|Services',
            sections: [
              {
                title: 'Cookie-Nutzung',
                description: 'Wir verwenden Cookies, um grundlegende Funktionen dieser Website zu ermöglichen und um dein Nutzungserlebnis zu verbessern.'
              },
              {
                title: 'Notwendige Cookies <span class="pm__badge">Immer aktiv</span>',
                description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
                linkedCategory: 'necessary',
                cookieTable: {
                  headers: {
                    name: 'Cookie',
                    domain: 'Domain',
                    desc: 'Beschreibung'
                  },
                  body: [
                    {
                      name: 'cc_cookie',
                      domain: 'zerofour04.dev',
                      desc: 'Speichert deine Cookie-Einstellungen'
                    },
                    {
                      name: '__cf_bm',
                      domain: 'cloudflare.com',
                      desc: 'Cloudflare Bot-Schutz'
                    },
                    {
                      name: 'cf_clearance',
                      domain: 'cloudflare.com',
                      desc: 'Cloudflare Turnstile (CAPTCHA-Schutz)'
                    }
                  ]
                }
              },
              {
                title: 'Analyse Cookies',
                description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem Informationen anonym gesammelt und gemeldet werden.',
                linkedCategory: 'analytics',
                cookieTable: {
                  headers: {
                    name: 'Cookie',
                    domain: 'Domain',
                    desc: 'Beschreibung'
                  },
                  body: [
                    {
                      name: '_ga',
                      domain: 'zerofour04.dev',
                      desc: 'Google Analytics - Unterscheidet Benutzer'
                    },
                    {
                      name: '_ga_*',
                      domain: 'zerofour04.dev',
                      desc: 'Google Analytics - Speichert Session-Daten'
                    }
                  ]
                }
              },
              {
                title: 'Weitere Informationen',
                description: 'Weitere Details findest du in unserer <a href="/privacy">Datenschutzerklärung</a> und im <a href="/imprint">Impressum</a>.'
              }
            ]
          }
        },
        en: {
          consentModal: {
            title: 'We use Cookies',
            description: 'This website uses cookies to improve user experience and analyze visitor statistics. You can adjust your settings at any time.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Necessary only',
            showPreferencesBtn: 'Settings',
            footer: '<a href="/privacy">Privacy Policy</a> <a href="/imprint">Imprint</a>'
          },
          preferencesModal: {
            title: 'Cookie Settings',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Necessary only',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            serviceCounterLabel: 'Service|Services',
            sections: [
              {
                title: 'Cookie Usage',
                description: 'We use cookies to enable basic functions of this website and to improve your user experience.'
              },
              {
                title: 'Necessary Cookies <span class="pm__badge">Always active</span>',
                description: 'These cookies are required for basic website functions and cannot be disabled.',
                linkedCategory: 'necessary',
                cookieTable: {
                  headers: {
                    name: 'Cookie',
                    domain: 'Domain',
                    desc: 'Description'
                  },
                  body: [
                    {
                      name: 'cc_cookie',
                      domain: 'zerofour04.dev',
                      desc: 'Stores your cookie preferences'
                    },
                    {
                      name: '__cf_bm',
                      domain: 'cloudflare.com',
                      desc: 'Cloudflare bot protection'
                    },
                    {
                      name: 'cf_clearance',
                      domain: 'cloudflare.com',
                      desc: 'Cloudflare Turnstile (CAPTCHA protection)'
                    }
                  ]
                }
              },
              {
                title: 'Analytics Cookies',
                description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
                linkedCategory: 'analytics',
                cookieTable: {
                  headers: {
                    name: 'Cookie',
                    domain: 'Domain',
                    desc: 'Description'
                  },
                  body: [
                    {
                      name: '_ga',
                      domain: 'zerofour04.dev',
                      desc: 'Google Analytics - Distinguishes users'
                    },
                    {
                      name: '_ga_*',
                      domain: 'zerofour04.dev',
                      desc: 'Google Analytics - Stores session data'
                    }
                  ]
                }
              },
              {
                title: 'More Information',
                description: 'For more details, please see our <a href="/privacy">Privacy Policy</a> and <a href="/imprint">Imprint</a>.'
              }
            ]
          }
        }
      }
    }
  });
}

export function hasAnalyticsConsent() {
  return CookieConsent.acceptedCategory('analytics');
}

export function showCookiePreferences() {
  CookieConsent.showPreferences();
}

export function setCookieConsentLanguage(locale) {
  CookieConsent.setLanguage(locale);
}
